<script lang="ts">
import { defineComponent, PropType, toRef, ref, watch, onMounted } from 'vue'
import AccordionBody from '@/components/accordion/AccordionBody.vue'
import MultiSelectorWithExcludeOption from '@/components/form/MultiSelectorWithExcludeOption.vue'
import InputLeadFormWrapper from '@/components/lead/InputLeadFormWrapper.vue'
import { formDefaultProps, formPartial } from '@/models/formPartial'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'MultiSelectorWithSecondaryItem',

  components: {
    AccordionBody,
    MultiSelectorWithExcludeOption,
    InputLeadFormWrapper,
  },

  props: {
    ...formDefaultProps,

    /**
     * <select> 要素の valueを受け取る
     * 値が null の場合は value 指定なしとして内部データで処理を行う
     */
    modelValue: {
      type: Array as PropType<MultiSelect>,
      default: () => [],
    },

    /**
     * <select> 要素の <option> 部分に渡す配列
     *
     * @description
     *  マルチセレクト自体は、単なる string 配列や number 配列も許容しているが、
     *  排他制御ありの本コンポーネントの場合、それらは許容しない。（isExclude オプションがある SelectItem 型のみ）
     */
    items: {
      type: Array as PropType<SelectItem[]>,
      required: true,
    },

    /**
     * <option> の末尾に付与される単位
     * 任意プロパティ
     *
     * @example unit="個" => <option>1個</option>
     */
    unit: {
      type: String,
      default: '',
    },

    /**
     * エラー発生状態かどうか
     * ※ エラー状態は外から受け取る
     */
    isError: {
      type: Boolean,
      default: false,
    },

    /**
     * 拡張質問項目に対応する親選択肢の value 値
     */
    primaryItemValuesForExpandAction: {
      type: Array as PropType<(string | number)[]>,
      default: () => [],
    },
  },

  emits: [
    'update:modelValue',
    'change',
    'select:primary',
    'open:secondary',
    'close:secondary'
  ],

  setup(props, { emit }) {
    const { hasError } = formPartial(props.name, toRef(props, 'validationScheme'))

    /**
     * コンポーネント内部でもつ選択状態の値
     * 親コンポーネントから value プロパティ を受け取らない場合は、このプロパティの値が selector に反映される
     */
    const localValue: Ref<MultiSelect> = ref([])
    const isShowSecondary = ref(false)

    watch(() => props.modelValue, (newValue) => {
      if (localValue.value !== newValue) localValue.value = newValue
    })

    /**
     * 「その他」に該当する項目が選択されていた状態から選択が外された場合に拡張アコーディオンを閉じる
     * @description
     *  通常、「その他」項目選択状態の切り替わりによるアコーディオン開閉制御は `onChange` 側で行われるが、
     *  「排他項目」選択による強制的な選択解除の際は watcher 側でハンドリングする
     */
    watch(localValue, (newValue, oldValue) => {
      if (!props.primaryItemValuesForExpandAction || props.primaryItemValuesForExpandAction.length === 0) return
      const isShowSecondaryAsNew = props.primaryItemValuesForExpandAction.some(val => (newValue as ReadonlyArray<string | number>).includes(val))
      const isShowSecondaryAsOld = props.primaryItemValuesForExpandAction.some(val => (oldValue as ReadonlyArray<string | number>).includes(val))

      if (!isShowSecondaryAsNew && isShowSecondaryAsOld) {
        isShowSecondary.value = false
      }
    })

    onMounted(() => {
      localValue.value = props.modelValue
      initSecondaryDisplayState()
    })

    /**
     * 選択された際の発火イベント
     * @param value 選択された項目の value
     * @param target 選択された項目の DOM
     *
     * @description
     *  Vue における emit の引数は暗黙的に受け取れます。
     *  複数の引数を受け取る場合、template 側の受け取り側は、明示的に `$event` を指定せず、暗黙的に受け取ることで複数キャッチすることができます。
     */
    const onChange = (value: string | number, target: HTMLInputElement) => {
      // 選択された項目が「拡張質問」に該当する項目だった場合は、親にそれを通知
      if (props.primaryItemValuesForExpandAction.includes(value)) {
        emit('select:primary', target.checked)
      }

      // 現在選択されている値を取得し、「拡張質問」に該当する項目だった場合は、表示状態を切り替える
      let selectedValues = (localValue.value as ReadonlyArray<string | number>)
      selectedValues = target.checked ? selectedValues.concat([value]) : selectedValues.filter(val => val !== value)
      isShowSecondary.value = props.primaryItemValuesForExpandAction.some(val => selectedValues.includes(val))
    }

    /**
     * アコーディオンが閉じきった際に発火するイベント
     *
     * @description
     *  追加質問項目の input オブジェクトを state から削除するタイミングは、
     *  親選択肢が外されたときではなく、アコーディオンが閉じきったあとに発火させないと、
     *  アコーディオンの「閉じる」トランジションが上手く動かなくなるため（state の情報を先に消すとアコーディオンの中身が消えてしまうため）、
     *  state からの削除処理は、明示的にトランジションが完了したあとに遅延発火させる
     */
    const onTransitionEnd = () => {
      const emitName = isShowSecondary.value
        ? 'open:secondary'
        : 'close:secondary'
      emit(emitName)
    }

    /**
     * 追加質問項目の表示状態を初期化
     *
     * 初期状態で「その他」に該当する選択肢が選ばられていたら、emit で input オブジェクトへの secondary のバインドをキックし、
     * アコーディオンの表示を true にする
     */
    const initSecondaryDisplayState = () => {
      if (props.primaryItemValuesForExpandAction.length > 0) {
        if (props.primaryItemValuesForExpandAction.some(val => (localValue.value as ReadonlyArray<string | number>).includes(val))) {
          emit('select:primary', true)
          isShowSecondary.value = true
        }
      }
    }

    return {
      // data
      localValue,
      isShowSecondary,

      // computed
      hasError,

      // methods
      onChange,
      onTransitionEnd,
    }
  }
})
</script>

<template>
  <InputLeadFormWrapper
    :model-value="modelValue"
    :header="header"
    :description="description"
    :namespace="namespace"
    :name="name"
    :is-disabled="isDisabled"
    :validation-scheme="validationScheme"
    is-select-type>
    <div class="l-unit__multiSelector">
      <div class="l-unit__multiSelector--description is-primary">
        <MultiSelectorWithExcludeOption
          :model-value="localValue"
          :items="items"
          :name="name"
          :unit="unit"
          :is-disabled="isDisabled"
          :is-error="hasError"
          @update:model-value="$emit('update:modelValue', $event)"
          @change="onChange" />
      <!-- /.l-unit__multiSelector description --></div>

      <AccordionBody
        class="l-unit__multiSelector--description is-secondary"
        :open-state="isShowSecondary"
        unit-of-visible-height="px"
        @transitionleave="onTransitionEnd">
        <div class="div">
          <slot />
        </div>
      <!-- /.l-unit__multiSelector description --></AccordionBody>
    <!-- /.l-unit__multiSelector --></div>
  </InputLeadFormWrapper>
</template>

<style lang="sass" scoped>
.l-unit__multiSelector
  & &--description
    &.is-secondary:not(:first-child)
      > .div
        padding-top: 0.5em  // margin だとアコーディオンの高さ計算に含まれなくなるので padding で設定すること
</style>
